import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Ruler, Users, Bed, ArrowLeft, Check } from "@phosphor-icons/react";
import Reveal from "../components/Reveal";
import BookingModal from "../components/BookingModal";
import PageSeo from "../components/PageSeo";
import { fetchRoom } from "../lib/api";
import { useLang } from "../context/LanguageContext";

export default function RoomDetailPage() {
  const { slug } = useParams();
  const { t, r, lang } = useLang();
  const [room, setRoom] = useState(null);
  const [bookOpen, setBookOpen] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchRoom(slug).then(setRoom).catch(() => {}); }, [slug]);

  if (!room) return <div className="pt-40 container-x text-white/60">...</div>;

  const name = lang === "it" ? room.name_it : room.name_en;
  const desc = lang === "it" ? room.description_it : room.description_en;
  const short = lang === "it" ? room.short_it : room.short_en;
  const badge = lang === "it" ? room.badge_it : room.badge_en;

  const seoTitle = (lang === "en" ? room.meta_title_en : room.meta_title_it) || `${name} — Hotel Alpi Resort Torino`;
  const seoDesc = (lang === "en" ? room.meta_desc_en : room.meta_desc_it) || short;
  const pathIt = `/it/camere/${room.slug_it}`;
  const pathEn = `/en/rooms/${room.slug_en}`;

  return (
    <div className="bg-[#09090b]" data-testid="room-detail-page">
      <PageSeo
        title={seoTitle}
        description={seoDesc}
        pathIt={pathIt}
        pathEn={pathEn}
        image={room.cover}
        type="article"
      />
      <section className="on-image relative h-[70svh] min-h-[520px]">
        <img src={room.cover} alt={name} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/90" />
        <div className="absolute inset-0 container-x flex flex-col justify-end pb-16">
          <Link to={r.rooms} className="overline text-white/70 inline-flex items-center gap-2 hover:text-[#C5A059] mb-6" data-testid="back-to-rooms">
            <ArrowLeft size={14} /> {t.nav.rooms}
          </Link>
          {badge && <div className="text-[10px] uppercase tracking-[0.25em] font-medium bg-[#C5A059] text-black px-3 py-1.5 inline-block self-start mb-4">{badge}</div>}
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-8xl leading-[0.95]">{name}</h1>
          <p className="mt-4 text-white/75 max-w-xl">{short}</p>
        </div>
      </section>

      <section className="py-24 lg:py-32 container-x">
        <div className="grid lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-7">
            <div className="overline mb-4">Overview</div>
            <p className="text-xl lg:text-2xl font-serif text-white/90 leading-relaxed">{desc}</p>
            <div className="mt-10 flex flex-wrap gap-8 text-sm text-white/70">
              <span className="inline-flex items-center gap-2"><Ruler size={18} className="text-[#C5A059]" /> {room.size_sqm} m²</span>
              <span className="inline-flex items-center gap-2"><Users size={18} className="text-[#C5A059]" /> {room.occupancy} {t.rooms.guests.toLowerCase()}</span>
              <span className="inline-flex items-center gap-2"><Bed size={18} className="text-[#C5A059]" /> {lang === "it" ? room.bed_type_it : room.bed_type_en}</span>
            </div>

            <div className="mt-12 grid sm:grid-cols-2 gap-4">
              {room.features?.map((f) => (
                <div key={f.icon + f.label_it} className="flex items-center gap-3 border-t border-white/10 pt-4">
                  <Check size={16} className="text-[#C5A059]" />
                  <span className="text-sm text-white/80">{lang === "it" ? f.label_it : f.label_en}</span>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal className="lg:col-span-5" delay={0.1}>
            <div className="sticky top-32 border border-white/10 p-8 bg-[#0c0c0e]">
              <div className="overline mb-2">{t.rooms.from}</div>
              <div className="font-serif text-5xl">€{room.price_from}</div>
              <div className="text-xs text-white/50 uppercase tracking-[0.2em] mt-1">{t.rooms.perNight}</div>
              <button
                onClick={() => setBookOpen(true)}
                className="mt-8 w-full btn-gold py-4 text-xs uppercase tracking-[0.2em]"
                data-testid="room-book-button"
              >
                {t.cta.bookRoom}
              </button>
              <p className="mt-6 text-xs text-white/50 leading-relaxed">
                {t.hero.micro}
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {room.gallery?.length > 0 && (
        <section className="py-24 container-x">
          <div className="overline mb-6">Gallery</div>
          <div className="grid md:grid-cols-3 gap-4">
            {room.gallery.map((g, i) => (
              <Reveal key={g} delay={i * 0.08}>
                <div className="card-hover aspect-[4/5] overflow-hidden border border-white/10">
                  <img src={g} alt="" className="w-full h-full object-cover" />
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      <BookingModal open={bookOpen} onOpenChange={setBookOpen} initialRoom={slug} />
    </div>
  );
}
